import React, { useEffect } from "react";
import "../viewRequestDetails/ViewRequestDetails.css";
import "../../../patientFullView/patientFullView.css";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Widgets from "../../../../widgets";
import Icons from "../../../../Assets/Icons";
import strings from "../../../../utils/strings";
import PatientPersonalInfo from "../../../patientFullView/patientPersonalInfo";
import AttachmentsSection from "../patientDetails/AttachmentsSection";
import { getNurseRequestDetails } from "../../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import { buildFileUrl, getFileType } from "../../../../utils/fileHelpers";

const formatDateTime = (dateTime) => {
  if (!dateTime) return "-";
  const d = new Date(dateTime);
  if (isNaN(d)) return String(dateTime);
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${date} at ${time}`;
};

// cancelledBy may come back as a populated user object ({_id, email, firstName, lastName}) rather than a string
const formatPersonName = (val) => {
  if (!val) return "-";
  if (typeof val === "string") return val;
  const first = typeof val.firstName === "string" ? val.firstName : val.firstName?.en || val.firstName?.vi || "";
  const last = typeof val.lastName === "string" ? val.lastName : val.lastName?.en || val.lastName?.vi || "";
  return `${first} ${last}`.trim() || val.name || val.email || "-";
};

const DeletedRequestDetails = () => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const returnTo = searchParams.get("returnTo");

  const { requestDetails } = useSelector((state) => state.manageRequest);

  const breadcrumbs = ["nursingCare", "manageRequests", "serviceDetails"];
  const paths = [strings.navigation.manageNurseReq, `${strings.navigation.manageNurseReq}?activeList=deletedRequests`, strings.navigation.viewDeletedRequestDetails];

  useEffect(() => {
    if (id) {
      dispatch(getNurseRequestDetails({ id, status: "CANCELLED" }));
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const request = requestDetails;

  const handleBack = () => {
    if (returnTo) {
      nav(decodeURIComponent(returnTo));
      return;
    }
    nav(`${strings.navigation.manageNurseReq}?activeList=deletedRequests`);
  };

  const patientData = {
    firstName: request?.patient?.fullName || "",
    lastName: "",
    hnNumber: request?.patient?.hnNumber,
    gender: request?.patient?.gender,
    dateOfBirth: request?.patient?.dateOfBirth,
    phoneNumber: request?.patient?.phoneNumber,
    countryCode: request?.patient?.countryCode,
    email: request?.patient?.email,
    insuranceCategory: request?.patient?.insuranceCategory,
    profilePhoto: request?.patient?.profilePhoto,
  };

  const formatAddress = (addr) => {
    if (!addr) return "-";
    const parts = [addr.street, addr.ward, addr.city, addr.province, addr.postalCode].filter(Boolean);
    return parts.join(", ");
  };

  const mapAttachments = (attachments) => {
    return (attachments || []).map((attachment) => {
      const rawUrl = typeof attachment === "string"
        ? attachment
        : attachment?.url || attachment?.path || attachment?.fileUrl || attachment?.filePath || attachment?.location || "";
      const name = typeof attachment === "string"
        ? rawUrl.split("/").pop().split("?")[0]
        : attachment?.name || attachment?.fileName || attachment?.originalName || rawUrl.split("/").pop().split("?")[0] || "file";
      const mimeType = typeof attachment === "string" ? "" : attachment?.mimeType || attachment?.mimetype || attachment?.contentType || attachment?.type;
      return { type: getFileType({ name, mimeType }), url: buildFileUrl(rawUrl), name };
    });
  };

  return (
    <div className="pdl-container service-details-wrapper">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />

      <div className="department-heading pdl">
        <div>{t("serviceDetails")}</div>
        <div className="pdl-buttons">
          <div className="pdl-btn verified pav-status" style={{ backgroundColor: "#fee2e2", color: "#ef4444", border: "none" }}>
            {request?.status ? t(request.status) : "-"}
          </div>
          <span className="req-id-text">{t("request_id")}: {request?.nursingRequestID || id}</span>
        </div>
      </div>

      <div className="pdl-info">
        <PatientPersonalInfo data={patientData} />

        <div className="pdl-body extra-patient-fields">
          <div className="extra-row address-zone">
            <div className="addr-field">
              <label>
                <img src={Icons.Location} alt="loc" width="16" />
                {t("patientAddress")}
              </label>
              <div className="addr-box">
                <input type="checkbox" readOnly checked={request?.address?.defaultAddress || false} />
                <span>{formatAddress(request?.address)}</span>
              </div>
            </div>
            <div className="zone-field">
              <label className="required">{t("zone")}</label>
              <Widgets.SelectDropdown value={request?.address?.zone || ""} placeholder={t("select")} disabled big />
            </div>
          </div>
        </div>
      </div>

      <AttachmentsSection attachments={mapAttachments(request?.attachments)} />

      <div className="pdl-info">
        <div className="notes-container no-bg-border">
          <div className="green-bar"></div>
          <div className="notes-content">
            <h4 className="notes-title" style={{ color: "#2d3436" }}>
              {t("description")}
            </h4>
            <p className="notes-text">{request?.description || "-"}</p>
          </div>
        </div>
      </div>

      {/* Cancellation Details */}
      <div className="pdl-info">
        <p className="pdl-title">{t("cancellationDetails")}</p>
        <div className="pdl-body flex-row">
          <div className="pref-box">
            <span className="pref-lbl">{t("cancellationReason")}</span>
            <span className="pref-val">{request?.cancellationReason || "-"}</span>
          </div>
          <div className="pref-box">
            <span className="pref-lbl">{t("cancelledBy")}</span>
            <span className="pref-val">{formatPersonName(request?.cancelledBy || request?.cmdCenterAdmin)}</span>
          </div>
          <div className="pref-box">
            <span className="pref-lbl">{t("cancelledAt")}</span>
            <span className="pref-val">{formatDateTime(request?.cancelledAt || request?.updatedAt)}</span>
          </div>
        </div>
      </div>

      <div className="form-section-footer">
        <button className="form-section-cancel" onClick={handleBack}>
          {t("back")}
        </button>
      </div>
    </div>
  );
};

export default DeletedRequestDetails;
